import React from "react";
import { motion } from "framer-motion";
import {
  ClipboardList,
  FolderLock,
  ActivitySquare,
  HardHat,
  Calculator,
  Building2,
} from "lucide-react";

// ── Accent colour used ONLY on the dark left panel ──────────────────────────
// var(--color-secondary) is dark red → invisible on dark bg.
// We use a warm amber that pops on dark navy regardless of the theme token.
const PANEL_ACCENT = "#E8A830";

const FEATURES = [
  {
    Icon: ClipboardList,
    title: "Centralized Project Oversight",
    desc: "Track estimates, assignments, and deadlines in one organized workspace.",
  },
  {
    Icon: FolderLock,
    title: "Secure Document Management",
    desc: "Store, organize, and share project documents securely with your team and clients.",
  },
  {
    Icon: ActivitySquare,
    title: "Real-Time Progress Tracking",
    desc: "Monitor project progress and keep clients informed at every stage.",
  },
];

const ROLES = [
  { Icon: HardHat,    label: "CONTRACTORS",  sub: "Strong Partnerships" },
  { Icon: Calculator, label: "ESTIMATORS",   sub: "Accurate. Organized." },
  { Icon: Building2,  label: "PROJECT TEAMS", sub: "Collaborate. Deliver." },
];

// ─── SVG construction scene ─────────────────────────────────────────────────
function ConstructionScene() {
  return (
    <svg
      className="absolute bottom-0 left-0 w-full pointer-events-none"
      viewBox="0 0 480 420"
      preserveAspectRatio="xMidYMax meet"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      {/* Buildings */}
      <rect x="0"   y="160" width="52" height="260" fill="white" fillOpacity="0.07" />
      <rect x="6"   y="180" width="7"  height="10"  fill="white" fillOpacity="0.18" />
      <rect x="20"  y="180" width="7"  height="10"  fill="white" fillOpacity="0.18" />
      <rect x="34"  y="180" width="7"  height="10"  fill="white" fillOpacity="0.18" />
      <rect x="6"   y="200" width="7"  height="10"  fill="white" fillOpacity="0.09" />
      <rect x="20"  y="200" width="7"  height="10"  fill="white" fillOpacity="0.09" />
      <rect x="34"  y="200" width="7"  height="10"  fill="white" fillOpacity="0.09" />

      <rect x="62"  y="210" width="38" height="210" fill="white" fillOpacity="0.06" />
      <rect x="110" y="110" width="68" height="310" fill="white" fillOpacity="0.08" />
      <rect x="120" y="130" width="9"  height="14"  fill="white" fillOpacity="0.18" />
      <rect x="138" y="130" width="9"  height="14"  fill="white" fillOpacity="0.18" />
      <rect x="156" y="130" width="9"  height="14"  fill="white" fillOpacity="0.18" />
      <rect x="120" y="154" width="9"  height="14"  fill="white" fillOpacity="0.09" />
      <rect x="138" y="154" width="9"  height="14"  fill="white" fillOpacity="0.09" />
      <rect x="156" y="154" width="9"  height="14"  fill="white" fillOpacity="0.09" />

      <rect x="346" y="180" width="54" height="240" fill="white" fillOpacity="0.07" />
      <rect x="408" y="140" width="72" height="280" fill="white" fillOpacity="0.08" />
      <rect x="420" y="158" width="9"  height="13"  fill="white" fillOpacity="0.16" />
      <rect x="438" y="158" width="9"  height="13"  fill="white" fillOpacity="0.16" />
      <rect x="456" y="158" width="9"  height="13"  fill="white" fillOpacity="0.16" />

      {/* Crane mast */}
      <rect x="276" y="52" width="6" height="368" fill="white" fillOpacity="0.16" />
      {/* Jib */}
      <rect x="160" y="52" width="260" height="5"  fill="white" fillOpacity="0.16" />
      {/* Counter-jib */}
      <rect x="160" y="52" width="116" height="4"  fill="white" fillOpacity="0.10" />
      {/* Pendant lines */}
      <line x1="279" y1="57" x2="395" y2="100" stroke="white" strokeOpacity="0.10" strokeWidth="1" />
      <line x1="279" y1="57" x2="360" y2="100" stroke="white" strokeOpacity="0.07" strokeWidth="1" />
      {/* Hook cable */}
      <line x1="390" y1="57" x2="390" y2="134" stroke="white" strokeOpacity="0.14" strokeWidth="1.2" />
      <circle cx="390" cy="138" r="5" fill="none" stroke="white" strokeOpacity="0.22" strokeWidth="1.5" />
      {/* Trolley */}
      <rect x="384" y="49" width="12" height="8" rx="2" fill="white" fillOpacity="0.18" />

      {/* Scaffolding */}
      <line x1="110" y1="200" x2="178" y2="200" stroke="white" strokeOpacity="0.09" strokeWidth="1" />
      <line x1="110" y1="230" x2="178" y2="230" stroke="white" strokeOpacity="0.09" strokeWidth="1" />
      <line x1="110" y1="260" x2="178" y2="260" stroke="white" strokeOpacity="0.09" strokeWidth="1" />
      <line x1="144" y1="110" x2="144" y2="420" stroke="white" strokeOpacity="0.06" strokeWidth="1" />

      {/* Ground */}
      <rect x="0" y="416" width="480" height="4" fill="white" fillOpacity="0.07" />

      {/* Warm amber horizon glow — matches PANEL_ACCENT */}
      <ellipse cx="240" cy="420" rx="280" ry="90" fill="url(#sunriseGrad)" />
      <defs>
        <radialGradient id="sunriseGrad" cx="50%" cy="100%" r="100%">
          <stop offset="0%"   stopColor={PANEL_ACCENT} stopOpacity="0.25" />
          <stop offset="100%" stopColor={PANEL_ACCENT} stopOpacity="0"    />
        </radialGradient>
      </defs>
    </svg>
  );
}

function BrandPanel() {
  return (
    <div className="relative z-10 flex flex-col justify-between h-full">

      {/* Logo */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-white/95 shadow-lg flex items-center justify-center overflow-hidden flex-shrink-0 p-1.5">
          <img
            src="/favicon.jpg"
            alt="Surya Group"
            className="w-full h-full object-contain"
          />
        </div>
        <div>
          <p className="text-white font-semibold text-[15px] leading-none tracking-tight">
            SwiftBoard
          </p>
          <p className="text-[11px] mt-[3px] font-medium" style={{ color: PANEL_ACCENT }}>
            by Surya Group
          </p>
        </div>
      </div>

      {/* Headline + Features */}
      <div className="flex flex-col gap-7">
        {/* Headline */}
        <div>
          <h2 className="text-4xl xl:text-[2.75rem] font-black text-white leading-[1.13] tracking-tight">
            Where Construction<br />
            Teams{" "}
            <span style={{ color: PANEL_ACCENT }}>Stay Aligned.</span>
          </h2>
          <p
            className="text-[13px] mt-4 leading-relaxed max-w-[300px]"
            style={{ color: "rgba(255,255,255,0.62)" }}
          >
            From estimating and project coordination to client communication and delivery tracking,
            Swift Board provides the structure growing construction businesses need to operate with confidence.
          </p>
        </div>

        {/* Feature list */}
        <div className="flex flex-col gap-4">
          {FEATURES.map(({ Icon, title, desc }) => (
            <div key={title} className="flex items-start gap-3">
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                style={{
                  background: `${PANEL_ACCENT}18`,
                  border: `1px solid ${PANEL_ACCENT}40`,
                }}
              >
                <Icon size={15} color={PANEL_ACCENT} strokeWidth={1.8} />
              </div>
              <div>
                <p className="font-semibold text-[13px] leading-snug" style={{ color: "rgba(255,255,255,0.95)" }}>
                  {title}
                </p>
                <p className="text-[11.5px] mt-0.5 leading-relaxed" style={{ color: "rgba(255,255,255,0.52)" }}>
                  {desc}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Role stats */}
      <div
        className="pt-5 grid grid-cols-3"
        style={{ borderTop: `1px solid rgba(255,255,255,0.12)` }}
      >
        {ROLES.map(({ Icon, label, sub }, i) => (
          <div
            key={label}
            className="flex flex-col items-center text-center gap-2 px-2"
            style={{
              borderRight: i < 2 ? "1px solid rgba(255,255,255,0.10)" : "none",
            }}
          >
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{
                background: `${PANEL_ACCENT}18`,
                border: `1px solid ${PANEL_ACCENT}40`,
              }}
            >
              <Icon size={18} color={PANEL_ACCENT} strokeWidth={1.6} />
            </div>
            <div>
              <p
                className="font-bold text-[9.5px] tracking-widest leading-none"
                style={{ color: "rgba(255,255,255,0.9)" }}
              >
                {label}
              </p>
              <p className="text-[10px] mt-1" style={{ color: "rgba(255,255,255,0.50)" }}>
                {sub}
              </p>
            </div>
          </div>
        ))}
      </div>

    </div>
  );
}

const DefaultFooter = () => (
  <p className="text-[11.5px] text-text_muted text-center lg:text-left">
    © {new Date().getFullYear()} SwiftBoard · Powered by{" "}
    <span className="text-secondary font-semibold">Surya Group</span>
  </p>
);

// The form side is a full-height sheet that overlaps the brand panel with a
// large rounded edge. `headerRight` slots into its top-right corner (e.g. an
// AuthHeaderLink) and `footer` replaces the default copyright row.
const AuthCard = ({
  children,
  maxWidth = "max-w-[380px]",
  headerRight,
  footer = <DefaultFooter />,
}) => {
  return (
    <div className="min-h-screen flex bg-primary">
      {/* ── LEFT — Brand panel ── */}
      <div className="hidden lg:block lg:w-[44%] xl:w-[40%] relative bg-primary overflow-hidden flex-shrink-0">

        {/* Blueprint grid texture */}
        <div
          className="absolute inset-0 opacity-[0.045]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)",
            backgroundSize: "36px 36px",
          }}
        />

        {/* Depth vignette */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 90% 80% at 0% 0%, rgba(0,0,0,0.38) 0%, transparent 55%), radial-gradient(ellipse 70% 60% at 100% 100%, rgba(0,0,0,0.2) 0%, transparent 55%)",
          }}
        />

        {/* Amber ambient glow at bottom */}
        <div
          className="absolute bottom-0 left-0 right-0 h-64 pointer-events-none"
          style={{
            background: `radial-gradient(ellipse 100% 100% at 50% 100%, ${PANEL_ACCENT}22 0%, transparent 70%)`,
          }}
        />

        {/* Construction SVG scene */}
        <ConstructionScene />

        {/* Decorative rings */}
        <div className="absolute -bottom-10 -right-10 w-56 h-56 rounded-full border border-white/[0.06] pointer-events-none" />
        <div className="absolute -bottom-20 -right-20 w-80 h-80 rounded-full border border-white/[0.04] pointer-events-none" />
        <div className="absolute -bottom-32 -right-32 w-[440px] h-[440px] rounded-full border border-white/[0.025] pointer-events-none" />
        <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full border border-white/[0.05] pointer-events-none" />

        {/* Sheet overlaps this panel by 3rem, so pad the content clear of it */}
        <div className="relative z-10 h-full p-10 xl:p-14 pr-20 xl:pr-24">
          <BrandPanel />
        </div>
      </div>

      {/* ── RIGHT — Form panel ── */}
      <div className="relative z-20 flex-1 flex flex-col bg-surface min-h-screen overflow-y-auto lg:-ml-12 lg:rounded-l-[48px] lg:shadow-[-12px_0_40px_rgba(0,0,0,0.18)]">
        {headerRight && (
          <div className="flex justify-end px-5 pt-5 sm:px-10 sm:pt-7 lg:px-12">
            {headerRight}
          </div>
        )}

        <div className="flex-1 flex flex-col justify-center items-center px-5 py-10 sm:px-10">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.38, ease: "easeOut" }}
            className={`w-full ${maxWidth}`}
          >
            {children}
          </motion.div>
        </div>

        {footer && (
          <div className="px-5 pb-5 sm:px-10 sm:pb-7 lg:px-12">{footer}</div>
        )}
      </div>
    </div>
  );
};

export default AuthCard;
